import React from "react";
import GenericTable from "./GenericTable";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { IconButton, Tooltip, Box, Chip, Stack, Switch } from "@mui/material";
import { CHANNEL_CARDS } from "../constants/channelCatalog";
import { useTranslation } from "react-i18next";

const iconForTipo = (tipo) => {
  const card = CHANNEL_CARDS.find((c) => c.id === Number(tipo));
  if (!card) return null;
  return card.icon({ sx: { color: card.color ?? "inherit" } });
};

const labelForTipo = (tipo, naLabel) => {
  const card = CHANNEL_CARDS.find((c) => c.id === Number(tipo));
  return card ? card.label : String(tipo ?? naLabel);
};

const hasImapAuthProblem = (channel) =>
  String(channel?.imap_poll_status || "").trim() === "auth_failed";

const hasRaPollProblem = (channel) =>
  ["auth_failed", "scrape_failed", "api_failed"].includes(
    String(channel?.ra_poll_status || "").trim()
  );

const raModeLabel = (channel, t) => {
  const mode = String(channel?.ra_mode || "").trim().toLowerCase();
  if (mode === "api") return t("channelTable.raModeApi", { defaultValue: "API" });
  if (mode === "scrape" || Number(channel?.tipo) === 46) {
    return t("channelTable.raModeScrape", { defaultValue: "Monitoramento" });
  }
  return null;
};

export default function ChannelTable({
  channels = [],
  rowCount,
  pageSize,
  page,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onEditChannel,
  onDeleteChannel,
  onDeleteSelected,
  onToggleStatus,
  isLoading,
}) {
  const { t } = useTranslation("settings");
  const na = t("shared.notApplicable");
  // Linhas só com campos "visuais" (não perdem performance),
  // mas guardamos o array original para buscar o objeto completo no clique
  const rows = channels.map((channel) => ({
    id: channel.id,
    nome: channel.nome || na,
    tipo: channel.tipo,
    tipoLabel: labelForTipo(channel.tipo, na),
    status: channel.status === 1 ? t("shared.active") : t("shared.inactive"),
    statusRaw: Number(channel.status) === 1 ? 1 : 0,
    isDefault: Number(channel.default) === 1,
    telefone: channel.telefone || "",
    email: channel.email || "",
    imapAuthProblem: hasImapAuthProblem(channel),
    raPollProblem: hasRaPollProblem(channel),
    raMode: raModeLabel(channel, t),
  }));

  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    {
      field: "nome",
      headerName: t("shared.name"),
      flex: 1.4,
      minWidth: 200,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          {iconForTipo(params.row.tipo)}
          <span title={params.value} style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {params.value}
          </span>
          {params.row.isDefault ? (
            <Tooltip title={t("channelTable.defaultTooltip")}>
              <Chip
                size="small"
                color="primary"
                label={t("channelTable.defaultBadge")}
                sx={{ height: 22, "& .MuiChip-label": { px: 0.75 } }}
              />
            </Tooltip>
          ) : null}
          {params.row.imapAuthProblem ? (
            <Tooltip title={t("channelTable.imapAuthProblemTooltip")}>
              <Chip
                size="small"
                color="error"
                icon={<ErrorOutlineRoundedIcon />}
                label={t("channelTable.imapAuthProblem")}
                sx={{ height: 22, "& .MuiChip-label": { px: 0.75 } }}
              />
            </Tooltip>
          ) : null}
          {params.row.raMode ? (
            <Chip
              size="small"
              variant="outlined"
              label={params.row.raMode}
              sx={{ height: 22, "& .MuiChip-label": { px: 0.75 } }}
            />
          ) : null}
          {params.row.raPollProblem ? (
            <Tooltip
              title={t("channelTable.raPollProblemTooltip", {
                defaultValue: "Falha no monitoramento Reclame Aqui",
              })}
            >
              <Chip
                size="small"
                color="warning"
                icon={<ErrorOutlineRoundedIcon />}
                label={t("channelTable.raPollProblem", { defaultValue: "RA poll" })}
                sx={{ height: 22, "& .MuiChip-label": { px: 0.75 } }}
              />
            </Tooltip>
          ) : null}
        </Stack>
      ),
    },
    { field: "tipoLabel", headerName: t("shared.type"), flex: 1, minWidth: 180 },
    {
      field: "status",
      headerName: t("shared.status"),
      width: 160,
      renderCell: (params) => {
        const full = channels.find((c) => Number(c.id) === Number(params.row.id)) || params.row;
        const isActive = Number(params.row.statusRaw) === 1;
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              size="small"
              checked={isActive}
              disabled={!onToggleStatus}
              onChange={() => onToggleStatus?.(full)}
            />
            <Chip
              size="small"
              label={params.row.status}
              color={isActive ? "success" : "default"}
              variant="outlined"
            />
          </Stack>
        );
      },
    },
    {
      field: "actions",
      headerName: t("shared.actions"),
      width: 160,
      renderCell: (params) => {
        // Busca o canal COMPLETO pelo id para editar com todos os campos
        const full = channels.find((c) => Number(c.id) === Number(params.row.id)) || params.row;
        return (
          <Box display="flex" gap={1}>
            <Tooltip title={t("shared.edit")}>
              <IconButton size="small" onClick={() => onEditChannel?.(full)}>
                <EditRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("shared.delete")}>
              <IconButton size="small" onClick={() => onDeleteChannel?.(params.row.id)}>
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <GenericTable
      rows={rows}
      columns={columns}
      rowCount={rowCount}
      pageSize={pageSize}
      page={page}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSearch={onSearch}
      isLoading={isLoading}
      onExportPDFTitle={t("channelTable.exportTitle")}
      exportFileName="canais"
      onDeleteSelected={onDeleteSelected}
      onRowClick={(row) => {
        const full = channels.find((c) => Number(c.id) === Number(row.id));
        if (full) onEditChannel?.(full);
      }}
    />
  );
}
